import { Controller } from 'react-hook-form';
import {
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import type { FieldConfig } from './FieldConfig';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import './formField.css';

type Props = {
  field: FieldConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  dateOfBirth?: boolean;
  minimumAgeRequired?: number;
};

export const TEXT_FIELD_ALLOWED_CHARACTERS_PATTERN =
  /^[\p{L}\p{N}\s.,'+?@&_()/:;-]*$/u;

const FormField = ({
  field,
  control,
  dateOfBirth = false,
  minimumAgeRequired,
}: Props) => {
  const formatPhoneNumber = (value: string) => {
    if (!value) return value;

    const phoneNumber = value.replaceAll(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const rules = {
    required: field.mandatory ? `${field.fieldLabel} is required` : false,
    ...(field.type === 'Text' && {
      maxLength: {
        value: 1000,
        message: `${field.fieldLabel} cannot exceed 1000 characters`,
      },
      validate: (value: string) =>
        TEXT_FIELD_ALLOWED_CHARACTERS_PATTERN.test(value ?? '') ||
        `Only these special characters are allowed: . , ' + ? @ & _ ( ) / : ; -`,
    }),
    ...((field.phone || field.type === 'PhoneNumber') && {
      validate: (value: string) => {
        if (!value) return true; // allow empty if not required

        const digits = value.replaceAll(/\D/g, '');

        if (digits.length !== 10) {
          return 'Phone number must be 10 digits';
        }

        // Optional: strict format check
        if (!/^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)) {
          return 'Phone must be in format (123) 456-7890';
        }

        return true;
      },
    }),
    ...(field.type === 'Checkbox' &&
      field.mandatory && {
        validate: (value: boolean | string) => {
          if (!value || value === '') {
            return `${field.fieldLabel} is required`;
          }
          return true;
        },
      }),
    ...(field.fieldName === 'dateOfBirth' && {
      validate: (value: string) => {
        if (!value) return true;

        const minimumAgeRequiredValue = minimumAgeRequired || 0;

        const selectedDate = moment(value);

        const minimumAllowedDate = moment().subtract(
          minimumAgeRequiredValue,
          'years'
        );

        if (selectedDate.isAfter(minimumAllowedDate, 'day')) {
          return `Minimum allowed age is ${minimumAgeRequired} year(s)
`;
        }

        return true;
      },
    }),
  };

  return (
    <Controller
      name={field.fieldName}
      control={control}
      rules={rules}
      render={({ field: rhfField, fieldState }) => {
        const error = fieldState.error?.message;

        switch (field.type) {
          case 'Text':
          case 'Number':
            return (
              <TextField
                {...rhfField}
                type={field.type.toLocaleLowerCase()}
                fullWidth
                size="small"
                value={rhfField.value ?? ''}
                error={!!error}
                helperText={error}
                placeholder={`Type ${field.fieldLabel}`}
                {...(field.type === 'Number' && { inputProps: { min: 0 } })}
                onKeyDown={(e) => {
                  if (
                    field.type === 'Number' &&
                    (e.key === '-' || e.key === 'e' || e.key === 'E')
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (field.type === 'Number' && Number(value) < 0) return;
                  if (field.phone) {
                    rhfField.onChange(formatPhoneNumber(value));
                  } else {
                    rhfField.onChange(value);
                  }
                }}
              />
            );

          case 'PhoneNumber':
            return (
              <TextField
                {...rhfField}
                type="tel"
                fullWidth
                size="small"
                value={rhfField.value ?? ''}
                error={!!error}
                helperText={error}
                placeholder="(123) 456-7890"
                onKeyDown={(e) => {
                  if (
                    !/^\d$/.test(e.key) &&
                    ![
                      'Backspace',
                      'Delete',
                      'ArrowLeft',
                      'ArrowRight',
                      'Tab',
                    ].includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  rhfField.onChange(formatPhoneNumber(e.target.value));
                }}
              />
            );

          case 'Dropdown': {
            // Normalize value to match available options (case-insensitive)
            const currentValue = rhfField.value ? String(rhfField.value) : '';

            // Find matching option (case-insensitive)
            const normalizedValue = (() => {
              if (!currentValue || !Array.isArray(field.fieldValues)) {
                return currentValue;
              }

              // Check for exact match first
              const exactMatch = field.fieldValues.find(
                (opt) => opt === currentValue
              );
              if (exactMatch) {
                return exactMatch;
              }

              // Check for case-insensitive match
              const caseInsensitiveMatch = field.fieldValues.find(
                (opt) => opt.toLowerCase() === currentValue.toLowerCase()
              );

              return caseInsensitiveMatch ?? currentValue;
            })();

            return (
              <TextField
                {...rhfField}
                select
                size="small"
                fullWidth
                value={normalizedValue}
                error={!!error}
                helperText={error}
                onChange={(e) => {
                  // Store the exact value from the dropdown option
                  rhfField.onChange(e.target.value);
                }}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return (
                          <span className="dropdown-placeholder">
                            {`Select ${field.fieldLabel}`}
                          </span>
                        );
                      }
                      return (
                        <span className="dropdown-selected-value">
                          {selected as string}
                        </span>
                      );
                    },
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            fontSize: '12px',
                            textAlign: 'left',
                          },
                        },
                      },
                    },
                  },
                }}
                className="dropdown-values"
              >
                {Array.isArray(field.fieldValues) &&
                  field.fieldValues.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
              </TextField>
            );
          }

          case 'Date':
            return (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={
                    rhfField.value
                      ? moment(moment.utc(rhfField.value).format('YYYY-MM-DD'))
                      : null
                  }
                  onChange={(value) =>
                    rhfField.onChange(value ? value.format('YYYY-MM-DD') : '')
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      error: !!error,
                      helperText: error,
                    },
                  }}
                  disableFuture={!!dateOfBirth}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '24px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                    },
                  }}
                />
              </LocalizationProvider>
            );

          case 'Checkbox':
            if (
              Array.isArray(field.fieldValues) &&
              field.fieldValues.length > 0
            ) {
              return (
                <FormControl error={!!error} fullWidth>
                  <FormGroup row>
                    {field.fieldValues.map((option) => {
                      // Case-insensitive comparison for checkbox selection
                      const currentValue = rhfField.value
                        ? String(rhfField.value)
                        : '';
                      const isChecked =
                        currentValue.toLowerCase() === option.toLowerCase();

                      return (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  rhfField.onChange(option);
                                } else {
                                  rhfField.onChange('');
                                }
                              }}
                            />
                          }
                          label={option}
                        />
                      );
                    })}
                  </FormGroup>
                  {error && <FormHelperText>{error}</FormHelperText>}
                </FormControl>
              );
            }
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(rhfField.value)}
                    onChange={(e) => rhfField.onChange(e.target.checked)}
                  />
                }
                label={field.fieldLabel}
              />
            );

          default:
            return <></>;
        }
      }}
    />
  );
};

export default FormField;
